import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge Tailwind CSS classes
 * Combines clsx and tailwind-merge for optimal class handling
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Format number with appropriate suffixes (K, M, B)
 * @param num - Number to format
 * @returns Formatted string
 */
export function formatNumber(num: number): string {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1) + 'B';
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

/**
 * @deprecated Use `formatCents()` from `@lib/currency` for API values (in cents),
 * or `formatUnits()` from `@lib/currency` for pre-converted dollar amounts.
 *
 * This function does NOT convert cents to dollars. Most API monetary values
 * are in cents, so passing them here displays 100x the correct amount.
 *
 * @param amount - Monetary amount as a decimal (e.g., 2500 = $2,500.00)
 * @param currency - ISO 4217 currency code (default: 'USD')
 * @param locale - BCP 47 locale tag (default: 'en-US')
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number,
  currency: string = 'USD',
  locale: string = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * @deprecated Use `formatUnits()` from `@lib/currency` instead.
 *
 * @param amount - Dollar amount (e.g. 50.00 for $50)
 * @param currency - ISO 4217 currency code (default: 'USD')
 * @param locale - BCP 47 locale (default: 'en-US')
 */
export function formatDollarAmount(
  amount: number,
  currency: string = 'USD',
  locale: string = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format a Date as an ISO 8601 UTC timestamp string for API payloads.
 * Normalizes to midnight UTC (T00:00:00.000Z) for date-only precision.
 *
 * @param date - Date to format (default: current date)
 * @returns ISO 8601 string, e.g. "2026-02-23T00:00:00.000Z"
 *
 * @example
 * toISOTimestamp();                          // "2026-02-23T00:00:00.000Z" (today)
 * toISOTimestamp(new Date('2026-03-15'));    // "2026-03-15T00:00:00.000Z"
 */
export function toISOTimestamp(date: Date = new Date()): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00.000Z`;
}

/**
 * Generate a random ID for temporary use
 * @param length - Length of the ID (default: 8)
 * @returns Random string ID
 */
export function generateId(length: number = 8): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Debounce function to limit function calls
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Throttle function to limit function calls
 * @param func - Function to throttle
 * @param limit - Time limit in milliseconds
 * @returns Throttled function
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}
